import { isPointInPolygon } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import { FilterStage, StageParameters } from '@data/Simulations/pipeline/Stage';
import { RaycastMeshData } from '@rendering/DesignSchemes/DesignSchemeUtils';
import { SensorData } from 'Simulations/SensorPoint';
import { Box3, Raycaster, Vector3 } from 'three';

export interface ShadowFilterStageParameters extends StageParameters {
    polygons: FVector[][];
    isInverted: boolean;
    raycastMeshes: RaycastMeshData[];
    globalBox?: Box3;
}

class ShadowFilterStage extends FilterStage<ShadowFilterStageParameters> {
    private raycaster = new Raycaster();
    private _origin = new Vector3();
    private _direction = new Vector3();

    protected override filter(sensorData: SensorData): boolean {
        const params = this.getParams();

        // 1) Keep sensors inside of DS Polygons:
        const position = sensorData.getPosition();
        const point2D = new FVector(position.x, position.y);
        for (let polygon of this.getParams().polygons)
            if (isPointInPolygon(point2D, polygon)) {
                return true;
            }

        // 2) Prepare ray:
        const pos = sensorData.getPosition();
        const dir = sensorData.getVectorialResult();

        this._origin.set(pos.x, pos.y, pos.z);
        this._direction.copy(dir).normalize();
        if (params.isInverted) this._direction.negate();

        this.raycaster.set(this._origin, this._direction);
        this.raycaster.firstHitOnly = true;

        const ray = this.raycaster.ray;

        // 3) Global AABB early-out (Ray doesn't intersect the G-AABB? It's free):
        if (!params.globalBox || !ray.intersectsBox(params.globalBox)) {
            return true;
        }

        // 4) Per-mesh AABB (Ray doesn't intersect the AABB? Continue) + BVH raycast:
        for (const { mesh, box } of params.raycastMeshes) {
            if (!ray.intersectsBox(box)) continue;

            // 5) BVH early-exit:
            const geom: any = mesh.geometry;
            if (geom?.boundsTree?.raycastFirst(ray)) {
                return false;
            }
        }

        return true;
    }
}

export default ShadowFilterStage;
